/**
 * Spare parts inventory: the parts table from inventory.json, stock status,
 * in-memory reservations, alternate stock and the out-of-range counter that
 * triggers an inventory check. Port of inventory-manager.js; the EventBus
 * listeners become methods the caller invokes, and time and randomness are
 * injected.
 */
import { INVENTORY } from './constants';

export interface InventoryPart {
  partNumber: string;
  name: string;
  description?: string;
  equipment: string;
  quantity: number;
  minStock: number;
  leadTimeDays: number;
  cost: number;
  location: string;
  alternateLocations?: string[];
  status: string;
  lastRestocked?: string;
  vendor?: string;
  onOrder?: boolean;
  expectedDelivery?: string;
}

export interface InventoryLocation {
  name: string;
  address: string;
  primary: boolean;
}

export interface Reservation {
  id: string;
  partNumber: string;
  reason: string;
  reservedAt: string;
  reservedBy: string;
  status: 'ACTIVE' | 'USED' | 'RELEASED';
  releasedAt?: string;
}

/** Shape of public/data/inventory.json. */
export interface InventoryData {
  parts: Record<string, InventoryPart>;
  locations: Record<string, InventoryLocation>;
  reservations: Reservation[];
}

export type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'ON_ORDER';

export interface InventoryStatus {
  partNumber: string;
  name: string;
  equipment: string;
  quantity: number;
  localStock: number;
  minStock: number;
  status: StockStatus;
  location: string;
  alternateLocations: string[];
  /** "N days". */
  leadTime: string;
  leadTimeDays: number;
  cost: number;
  onOrder: boolean;
  expectedDelivery: string | null;
}

export interface PartSummary {
  partNumber: string;
  name: string;
  equipment: string;
  quantity: number;
  minStock: number;
  status: StockStatus;
  location: string;
  cost: number;
}

export interface AlternateStock {
  location: string;
  estimatedQuantity: number;
  transferTime: string;
}

/** Quantity 0 is ON_ORDER when on order else OUT_OF_STOCK; at or below the CONSTANTS thresholds (1, 2) LOW_STOCK; else IN_STOCK. */
export function getStockStatus(part: Pick<InventoryPart, 'quantity' | 'onOrder'>): StockStatus {
  if (part.quantity === 0) return part.onOrder ? 'ON_ORDER' : 'OUT_OF_STOCK';
  if (part.quantity <= INVENTORY.CRITICAL_STOCK_THRESHOLD) return 'LOW_STOCK';
  if (part.quantity <= INVENTORY.LOW_STOCK_THRESHOLD) return 'LOW_STOCK';
  return 'IN_STOCK';
}

/** The badge rule the UI used instead (quantity against the part's minStock). */
export function uiStockStatus(quantity: number, minStock: number): 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' {
  if (quantity === 0) return 'OUT_OF_STOCK';
  if (quantity <= minStock) return 'LOW_STOCK';
  return 'IN_STOCK';
}

/**
 * Equipment key by keyword in a subsystem id: fan/cooling, lna, hpa/sspa,
 * modem, antenna/az/el, switch/network. "modem" is tested before the
 * antenna keywords (defect fix: "satellite modem" contains "el").
 */
export function guessEquipmentFromId(subsystemId: string): string | null {
  const idLower = subsystemId.toLowerCase();
  if (idLower.indexOf('fan') !== -1 || idLower.indexOf('cooling') !== -1) return 'cooling_fan';
  if (idLower.indexOf('lna') !== -1) return 'lna';
  if (idLower.indexOf('hpa') !== -1 || idLower.indexOf('sspa') !== -1) return 'hpa';
  if (idLower.indexOf('modem') !== -1) return 'modem';
  if (idLower.indexOf('antenna') !== -1 || idLower.indexOf('az') !== -1 || idLower.indexOf('el') !== -1) return 'antenna';
  if (idLower.indexOf('switch') !== -1 || idLower.indexOf('network') !== -1) return 'network_switch';
  return null;
}

export function getStatusColor(status: string): string {
  return INVENTORY.STATUS_COLORS[status] || INVENTORY.STATUS_COLORS.IN_STOCK;
}

export function getStatusDisplay(inventoryStatus: Pick<InventoryStatus, 'status' | 'quantity' | 'expectedDelivery'> | null): string {
  if (!inventoryStatus) return 'Unknown';
  switch (inventoryStatus.status) {
    case 'IN_STOCK':
      return inventoryStatus.quantity + ' in stock';
    case 'LOW_STOCK':
      return 'Low stock (' + inventoryStatus.quantity + ')';
    case 'OUT_OF_STOCK':
      return 'Out of stock';
    case 'ON_ORDER':
      return 'On order - ETA: ' + inventoryStatus.expectedDelivery;
    default:
      return inventoryStatus.status;
  }
}

/** A sensor is tracked for out-of-range counting when its `${subsystemId}_${dataId}` contains an affected sensor id. */
export function isSensorAffected(subsystemId: string, dataId: string, affectedSensors: readonly string[]): boolean {
  if (affectedSensors.length === 0) return false;
  const idLower = (subsystemId + '_' + dataId).toLowerCase();
  return affectedSensors.some((affected) => idLower.indexOf(affected.toLowerCase()) !== -1);
}

export interface ReadingUpdate {
  subsystemId: string;
  dataId: string;
  status: string;
}

export interface OorTrigger {
  subsystemId: string;
  dataId: string;
  inventory: InventoryStatus;
}

export interface AlertForInventory {
  severity: string;
  equipment?: string;
  subsystemId?: string;
}

export interface InventoryCheck {
  equipment: string;
  status: InventoryStatus;
  /** Set when a CRITICAL alert auto-reserved the primary spare. */
  reservation: Reservation | null;
}

export interface InventoryManagerDeps {
  nowMs: () => number;
  random: () => number;
}

export interface InventoryManager {
  isLoaded(): boolean;
  /** The primary (first listed) spare for an equipment key. */
  getInventoryForEquipment(equipment: string): InventoryStatus | null;
  getInventoryBySubsystemId(subsystemId: string): InventoryStatus | null;
  getAllParts(): PartSummary[];
  getLowStockParts(): PartSummary[];
  getPart(partNumber: string): InventoryPart | null;
  /** Decrements the quantity and records an ACTIVE reservation; null when out of stock. */
  reservePart(partNumber: string, reason: string): Reservation | null;
  /** USED keeps the part out; RELEASED returns it to stock. */
  releaseReservation(reservationId: string, used: boolean): boolean;
  getActiveReservations(): Reservation[];
  getLocations(): Record<string, InventoryLocation>;
  /** Random 0 to 2 units at each alternate location, "2-5 days". */
  findAlternateStock(partNumber: string): AlternateStock[];
  /** The alert:triggered rule: CRITICAL or WARNING alerts look the equipment up; CRITICAL with stock auto-reserves. */
  checkInventoryForAlert(alert: AlertForInventory): InventoryCheck | null;
  /** The livedata:update rule: counts consecutive yellow/red readings of scenario-affected sensors; the third triggers. */
  recordReading(update: ReadingUpdate, affectedSensors: readonly string[]): OorTrigger | null;
  resetOutOfRange(update: Pick<ReadingUpdate, 'subsystemId' | 'dataId'>): void;
  /** Adds inventoryStatus to predictions whose equipment name maps to a stocked part. */
  attachInventoryToPredictions<T extends { equipment: string; inventoryStatus?: InventoryStatus }>(predictions: T[]): T[];
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function createInventoryManager(data: InventoryData, deps: InventoryManagerDeps): InventoryManager {
  const inventoryData: InventoryData = clone(data);
  inventoryData.reservations = inventoryData.reservations || [];
  const equipmentPartMap: Record<string, string[]> = {};
  for (const partNumber of Object.keys(inventoryData.parts)) {
    const equipment = inventoryData.parts[partNumber].equipment;
    (equipmentPartMap[equipment] ||= []).push(partNumber);
  }
  const oorCounters: Record<string, number> = {};

  function getPart(partNumber: string): InventoryPart | null {
    return inventoryData.parts[partNumber] || null;
  }

  function getInventoryForEquipment(equipment: string): InventoryStatus | null {
    const partNumbers = equipmentPartMap[equipment];
    if (!partNumbers || partNumbers.length === 0) return null;
    const part = inventoryData.parts[partNumbers[0]];
    if (!part) return null;
    return {
      partNumber: part.partNumber,
      name: part.name,
      equipment: part.equipment || equipment,
      quantity: part.quantity,
      localStock: part.quantity,
      minStock: part.minStock,
      status: getStockStatus(part),
      location: part.location,
      alternateLocations: part.alternateLocations || [],
      leadTime: part.leadTimeDays + ' days',
      leadTimeDays: part.leadTimeDays,
      cost: part.cost,
      onOrder: part.onOrder || false,
      expectedDelivery: part.expectedDelivery || null,
    };
  }

  function getAllParts(): PartSummary[] {
    return Object.keys(inventoryData.parts).map((partNumber) => {
      const part = inventoryData.parts[partNumber];
      return {
        partNumber,
        name: part.name,
        equipment: part.equipment,
        quantity: part.quantity,
        minStock: part.minStock,
        status: getStockStatus(part),
        location: part.location,
        cost: part.cost,
      };
    });
  }

  function reservePart(partNumber: string, reason: string): Reservation | null {
    const part = getPart(partNumber);
    if (!part || part.quantity <= 0) return null;
    const reservation: Reservation = {
      id: 'RES-' + deps.nowMs(),
      partNumber,
      reason,
      reservedAt: new Date(deps.nowMs()).toISOString(),
      reservedBy: 'System',
      status: 'ACTIVE',
    };
    part.quantity--;
    part.status = getStockStatus(part);
    inventoryData.reservations.push(reservation);
    return reservation;
  }

  function releaseReservation(reservationId: string, used: boolean): boolean {
    const index = inventoryData.reservations.findIndex((r) => r.id === reservationId && r.status === 'ACTIVE');
    if (index === -1) return false;
    const reservation = inventoryData.reservations[index];
    reservation.status = used ? 'USED' : 'RELEASED';
    reservation.releasedAt = new Date(deps.nowMs()).toISOString();
    if (!used) {
      const part = getPart(reservation.partNumber);
      if (part) {
        part.quantity++;
        part.status = getStockStatus(part);
      }
    }
    return true;
  }

  function checkInventoryForAlert(alert: AlertForInventory): InventoryCheck | null {
    if (alert.severity !== 'CRITICAL' && alert.severity !== 'WARNING') return null;
    const equipment = guessEquipmentFromId(alert.equipment || alert.subsystemId || '');
    if (!equipment) return null;
    const status = getInventoryForEquipment(equipment);
    if (!status) return null;
    let reservation: Reservation | null = null;
    if (INVENTORY.AUTO_RESERVE_ON_ALERT && alert.severity === 'CRITICAL' && status.quantity > 0) {
      reservation = reservePart(status.partNumber, 'Auto-reserved for critical alert');
    }
    return { equipment, status, reservation };
  }

  function recordReading(update: ReadingUpdate, affectedSensors: readonly string[]): OorTrigger | null {
    const key = update.subsystemId + '_' + update.dataId;
    if (update.status !== 'yellow' && update.status !== 'red') {
      oorCounters[key] = 0;
      return null;
    }
    if (!isSensorAffected(update.subsystemId, update.dataId, affectedSensors)) return null;
    oorCounters[key] = (oorCounters[key] || 0) + 1;
    if (oorCounters[key] < INVENTORY.CONSECUTIVE_OOR_TRIGGER) return null;
    oorCounters[key] = 0;
    const equipment = guessEquipmentFromId(update.subsystemId);
    if (!equipment) return null;
    const status = getInventoryForEquipment(equipment);
    if (!status) return null;
    return { subsystemId: update.subsystemId, dataId: update.dataId, inventory: status };
  }

  return {
    isLoaded: () => true,
    getInventoryForEquipment,
    getInventoryBySubsystemId: (subsystemId) => {
      const equipment = guessEquipmentFromId(subsystemId);
      return equipment ? getInventoryForEquipment(equipment) : null;
    },
    getAllParts,
    getLowStockParts: () => getAllParts().filter((p) => p.status === 'LOW_STOCK' || p.status === 'OUT_OF_STOCK'),
    getPart,
    reservePart,
    releaseReservation,
    getActiveReservations: () => inventoryData.reservations.filter((r) => r.status === 'ACTIVE'),
    getLocations: () => inventoryData.locations || {},
    findAlternateStock: (partNumber) => {
      const part = getPart(partNumber);
      if (!part || !part.alternateLocations) return [];
      return part.alternateLocations.map((loc) => ({
        location: loc,
        estimatedQuantity: Math.floor(deps.random() * 3),
        transferTime: '2-5 days',
      }));
    },
    checkInventoryForAlert,
    recordReading,
    resetOutOfRange: (update) => {
      oorCounters[update.subsystemId + '_' + update.dataId] = 0;
    },
    attachInventoryToPredictions: (predictions) => {
      for (const prediction of predictions) {
        const equipment = guessEquipmentFromId(prediction.equipment);
        if (!equipment) continue;
        const status = getInventoryForEquipment(equipment);
        if (status) prediction.inventoryStatus = status;
      }
      return predictions;
    },
  };
}
